import { useEffect, useRef, useCallback, useState } from 'react';
import type { ClientMessage, ServerMessage } from '@auction/shared';

// Use workers.dev domain for WebSocket (custom domain routes have WebSocket issues)
const WS_BASE = import.meta.env.VITE_WS_URL || 'wss://auction-actors-api.josej-alvarezm.workers.dev';

interface BidData {
  auctionId: string;
  bid: {
    bidderId: string;
    bidderName: string;
    amount: number;
    timestamp: Date | string;
  };
  newEndTime?: Date | string;
}

interface UseWebSocketOptions {
  auctionId: string;
  onBid?: (data: BidData) => void;
  onOutbid?: (data: { auctionId: string; yourBid: number; winningBid: number }) => void;
  onAuctionEnded?: (data: { auctionId: string; winnerId: string | null; finalPrice: number | null }) => void;
  onAuctionExtended?: (data: { auctionId: string; newEndTime: Date | string }) => void;
  onError?: (message: string) => void;
  onConnectionChange?: (connected: boolean) => void;
}

export function useWebSocket({
  auctionId,
  onBid,
  onOutbid,
  onAuctionEnded,
  onAuctionExtended,
  onError,
  onConnectionChange,
}: UseWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<ServerMessage | null>(null);
  
  // Store callbacks in refs to avoid triggering reconnections when they change
  const callbacksRef = useRef({ onBid, onOutbid, onAuctionEnded, onAuctionExtended, onError, onConnectionChange });
  callbacksRef.current = { onBid, onOutbid, onAuctionEnded, onAuctionExtended, onError, onConnectionChange };

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(`${WS_BASE}/api/ws/${auctionId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[WebSocket] Connected to auction:', auctionId);
      setIsConnected(true);
      callbacksRef.current.onConnectionChange?.(true);

      // Subscribe to auction updates
      const subscribeMsg: ClientMessage = { type: 'subscribe', auctionId };
      ws.send(JSON.stringify(subscribeMsg));

      // Start ping interval to keep connection alive
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        console.log('[WebSocket] Raw message received:', event.data);
        const message = JSON.parse(event.data) as ServerMessage;
        console.log('[WebSocket] Parsed message:', message);
        setLastMessage(message);

        switch (message.type) {
          case 'bid':
            console.log('[WebSocket] Calling onBid handler with:', message);
            callbacksRef.current.onBid?.(message);
            break;
          case 'outbid':
            callbacksRef.current.onOutbid?.(message);
            break;
          case 'auction_ended':
            callbacksRef.current.onAuctionEnded?.(message);
            break;
          case 'auction_extended':
            callbacksRef.current.onAuctionExtended?.(message);
            break;
          case 'error':
            callbacksRef.current.onError?.(message.message);
            break;
          case 'pong':
            // Keep-alive response
            break;
          default:
            console.log('[WebSocket] Unknown message type:', message);
        }
      } catch (err) {
        console.error('[WebSocket] Failed to parse message:', err);
      }
    };

    ws.onclose = (event) => {
      console.log('[WebSocket] Disconnected:', event.code, event.reason);
      setIsConnected(false);
      callbacksRef.current.onConnectionChange?.(false);

      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Attempt reconnection after 3 seconds (unless intentionally closed)
      if (event.code !== 1000) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('[WebSocket] Attempting reconnect...');
          connect();
        }, 3000);
      }
    };

    ws.onerror = (error) => {
      console.error('[WebSocket] Error:', error);
      callbacksRef.current.onError?.('WebSocket connection error');
    };
  }, [auctionId]); // Only reconnect when auctionId changes

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }
  }, []);

  const send = useCallback((message: ClientMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    isConnected,
    lastMessage,
    send,
    reconnect: connect,
    disconnect,
  };
}
